import logger from '../../../utils/logger-rum';
/* eslint-disable no-param-reassign */
import React, {
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import type { OnsiteLocation, OnsiteLocationStream, SrtStream } from '../../../stores/types/video';
import { SrtStreamStatus } from '../../../stores/types/video';
import useDragItem from '../../../hooks/use-dnd/use-drag-item';
import { DRAG_TYPE } from '../../../stores/reducers/user-interface';
import { CssPosition, GreenroomState as GS } from '../../../stores/types/types';
import { ThumbnailLocation } from '../../../types/enums';
import useShots from '../../../contexts/shots/provider';
import { FEED_TYPES } from '../../../stores/reducers/shots';
import DefaultSafetyImage from '../../../images/onsite-safety-image.png';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import Portal from '../../general-components/portal';
import ContextMenu from '../../context-menu/context-menu';
import { MenuItem } from '../../general-components/right-click-menu';
import { DeleteOnsiteLocation, DeleteSrtStream, UpdateOnsiteLocation, UpdateSrtStream } from '../../../connection/video';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../../stores/types/tabs';
import { setMetaMenuRoute, setMetaMenuState } from '../../../stores/actions/tabs';
import { setSelectedOnsiteLocationId } from '../../../stores/actions/video';
import { showAlert } from '../../general-components/alert-service';
import { cleanupVideoStreamElement } from '../../../utils/video/video-streams';
import { AVContext } from '../../../contexts/audio-video';
import useUserActions from '../../../hooks/use-user-actions';
import FeedDebugOverlay from '../../right-panel/video-debug-ui';
import ButtonClose from '../../general-components/button-close';
import useClickAwayListener from '../../../hooks/use-click-away-listener';
import useAudioSink from '../../../hooks/devices/use-audio-sink';
import Hls from 'hls.js';
import useEchoCancellation, { shouldDoEchoCancel } from '../../../hooks/use-echo-cancellation';

const SRT_PREVIEW_HEIGHT = 217; // from .srt-preview { height: 217px; }
const SRT_PREVIEW_WIDTH = 314; // from .srt-preview { width: 314px; }
const SRT_PREVIEW_GAP = 8;

const doEchoCancel = shouldDoEchoCancel();

interface SrtPreviewProps {
	srtStream: SrtStream;
}

function SrtPreview({ srtStream }: SrtPreviewProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const hlsRef = useRef<Hls | null>(null);

	const { applySinkId } = useAudioSink();
	useEchoCancellation(videoRef, audioRef);

	useEffect(() => {
		if (videoRef?.current) {
			applySinkId(videoRef.current);
		}
	}, [applySinkId, videoRef?.current]);

	useEffect(() => {
		if (videoRef.current && srtStream) {
			const src = `https://stream.mux.com/${srtStream.playbackId}.m3u8?min_resolution=480p`;

			if (Hls.isSupported()) {
				hlsRef.current = new Hls();
				hlsRef.current.loadSource(src);
				hlsRef.current.attachMedia(videoRef.current);

				hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
					logger.error('location srt stream hls error', {
						location: 'onsite-location-thumbnail',
						id: srtStream.id,
						playbackId: srtStream.playbackId,
						event,
						data,
					});
				});

				return () => {
					if (hlsRef.current) {
						hlsRef.current.destroy();
						hlsRef.current = null;
					}
				};
			}
		}

		return undefined;
	}, [srtStream?.playbackId, srtStream?.id]);

	return (
		<div className="srt-preview-video-wrapper">
			<video
				id={`srt-stream-preview-${srtStream.id}`}
				ref={videoRef}
				playsInline={true}
				muted={false}
				loop={false}
				poster={srtStream.thumbnail}
				onLoadedData={() => {
					if (doEchoCancel && audioRef.current) {
						audioRef.current.volume = 1;
					}
					if (!doEchoCancel && videoRef.current) {
						videoRef.current.volume = 1;
					}
					if (videoRef.current) {
						videoRef.current?.play().catch((e) => {
							logger.error('location srt stream onLoadedData play error', {
								location: 'feed-onsite-location',
								id: srtStream.id,
								playbackId: srtStream.playbackId,
							}, e);
						});
					}
				}}
				onPause={() => {
					// always resume playing if paused
					if (videoRef.current && !videoRef.current.ended) {
						videoRef.current.play().catch((e) => {
							logger.error('location srt stream onPause play error', {
								location: 'feed-onsite-location',
								id: srtStream.id,
								playbackId: srtStream.playbackId,
							}, e);
						});
					}
				}}
			/>
			{doEchoCancel && (
				<audio
					id={`srt-preview-audio-${srtStream.id}`}
					ref={audioRef}
					is="advanced-audio"
				/>
			)}
		</div>
	);
}

interface Props {
	onsiteLocation: OnsiteLocation;
	location: ThumbnailLocation;
	locationStream?: OnsiteLocationStream | undefined;
	srtStream?: SrtStream | undefined;
}

export default function OnsiteLocationThumbnail(props: Props) {
	const {
		onsiteLocation,
		location,
		locationStream,
		srtStream,
	} = props;

	const {
		id,
		locationName,
		pathName,
		safetyImage,
		advancedAudio,
		useSrt,
		srtId,
		resolution1080p
	} = onsiteLocation;

	const {
		FeedsTab,
		ShotModal,
	} = ThumbnailLocation;

	const dispatch = useDispatch();

	const menuDotsRef = useRef<HTMLDivElement | null>(null);
	const srtPreviewRef = useRef<HTMLDivElement | null>(null);
	const videoThumbRef = useRef<HTMLDivElement | null>(null);

	const [showMenu, setShowMenu] = useState<boolean>(false);
	const [menuPosition, setMenuPosition] = useState<CssPosition>({});
	const [showSrtPreview, setShowSrtPreview] = useState<boolean>(false);
	const [srtPreviewCoords, setSrtPreviewCoords] = useState<CssPosition>({});

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);

	const { getAV } = useContext(AVContext);

	const {
		shotModalShot: {
			shot_data: {
				shotFeeds: modalShotFeeds,
			},
		},
	} = useSelector((s: GS) => s.ShotsReducer);

	const {
		currentShot: {
			shot_data: {
				shotFeeds: currentShotFeeds,
			},
		},
		stripFeedFromShots,
	} = useShots();

	const {
		video: originalHasVideo,
		audio: originalHasAudio,
	} = getAV(locationStream?.id ?? '');

	const hasVideo = srtStream ? !srtStream.videoMuted : originalHasVideo;
	const hasAudio = srtStream ? !srtStream.muted : originalHasAudio;

	const {
		toggleAudio,
		toggleVideo,
	} = useUserActions();

	useClickAwayListener({
		ref: srtPreviewRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setShowSrtPreview(false),
		open: showSrtPreview,
	});

	const canDrag = useMemo(
		() => host && (location === FeedsTab || location === ShotModal),
		[host, location],
	);

	const feeds = useMemo(
		() => (location === ThumbnailLocation.ShotModal ? modalShotFeeds : currentShotFeeds),
		[location, modalShotFeeds, currentShotFeeds],
	);

	const inShot = useMemo(
		() => feeds.some((f) => f.feedType === FEED_TYPES.ONSITE_LOCATION && f.onsiteLocationId === id),
		[feeds],
	);

	const isSrtLocation = useMemo(() => useSrt || !!srtStream, [useSrt, srtStream]);

	const {
		dragRef,
		isDragging,
	} = useDragItem({ id, type: DRAG_TYPE.FEED_ONSITE_LOCATION }, canDrag);

	const handleShowMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (host) {
			event.preventDefault();
			event.stopPropagation();

			let dotRect: DOMRect | null = null;
			if (event.type === 'contextmenu' || event.button === 2) {
				// right click: use mouse position instead of dots
				dotRect = {
					left: event.clientX,
					top: event.clientY,
					width: 0,
					height: 0,
					right: event.clientX,
					bottom: event.clientY,
					x: event.clientX,
					y: event.clientY,
					toJSON: () => ({}),
				};
			} else if (menuDotsRef.current) {
				dotRect = menuDotsRef.current.getBoundingClientRect();
			}

			if (dotRect) {
				const dropHeight = menuItems.length * 36;
				const dropWidth = 164;
				const dropMargin = 4;
				const menuWidth = 84;

				// check if dropdown will be visible in drawer
				const inDrawerHorizontal = !(
					dotRect.left - dropMargin + dropWidth + menuWidth > window.innerWidth
				);

				// check if dropdown will be visible in drawer
				const inDrawerVertical = !(
					dotRect.top + dropMargin + dotRect.height + dropHeight > window.innerHeight
				);

				setMenuPosition({
					top: inDrawerVertical
						? dotRect.top + dropMargin + dotRect.height + dropHeight
						: dotRect.top,
					left: inDrawerHorizontal
						? dotRect.left - dropMargin
						: dotRect.left + dropMargin + dotRect.width - dropWidth,
				});
			}

			setShowMenu((cur) => !cur);
		}
	};

	const handleEditLocation = (locationId: number) => {
		dispatch(setSelectedOnsiteLocationId(locationId));
		dispatch(setMetaMenuRoute(META_MENU_ROUTES.ONSITE_LOCATION));
		dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
	};

	const handleCopyLocation = () => {
		if (!meeting) return;
		const locationUrl = `${window.location.origin}/${pathName}/${meeting.meeting_id}`;
		navigator.clipboard.writeText(locationUrl);
		showAlert({
			message: 'Link copied',
			description: 'Onsite URL is attached to your clipboard',
			type: 'general-notification',
			duration: 3000,
		});
	};

	const handleToggleSetting = async (
		locationId: number,
		oldSetting: boolean,
		field: 'advancedAudio' | 'resolution1080p'
	) => {
		if (!token || !meeting) return false;
		return !!await UpdateOnsiteLocation(
			token,
			meeting.meeting,
			locationId,
			{ [field]: !oldSetting },
		);
	};

	const handleDeleteLocation = async (locationId: number) => {
		if (!token || !meeting) return false;
		stripFeedFromShots(locationId, FEED_TYPES.ONSITE_LOCATION);
		// if an srt stream has been created for this location, delete it
		if (srtId) await DeleteSrtStream(token, meeting.meeting, srtId);
		return !!await DeleteOnsiteLocation(token, meeting.meeting, locationId);
	};

	const handleOpenLocationUrl = () => {
		if (!meeting) return;
		document.location.href = `${window.location.origin}/${pathName}/${meeting.meeting_id}`;
	};

	const handlePlaying = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
		if (e.target instanceof HTMLVideoElement) {
			e.target.volume = 0;
		}
	};

	const handleUpdateSrtStream = (srtProps: Partial<SrtStream>) => {
		if (!token || !meeting || !srtStream) return;
		UpdateSrtStream(
			token,
			meeting.meeting,
			srtStream.id,
			srtProps,
		);
	};

	const calculateSrtPreviewPosition = () => {
		const element = videoThumbRef.current;
		if (!element) return;

		const elementRect = element.getBoundingClientRect();

		const container = element.closest('.video-thumbs');
		if (!container) return;
		const containerRect = container.getBoundingClientRect();

		// always above in shot tab, otherwise only above if there is enough space to fit the preview
		const shouldBeBelow = elementRect.top - containerRect.top < SRT_PREVIEW_HEIGHT && location !== ThumbnailLocation.ShotTab;

		const top = shouldBeBelow
			? elementRect.bottom - containerRect.top + SRT_PREVIEW_GAP
			: elementRect.top - containerRect.top - SRT_PREVIEW_HEIGHT - SRT_PREVIEW_GAP;

		const containerWidth = containerRect.width;
		const elementLeftInContainer = elementRect.left - containerRect.left;
		const elementRightInContainer = elementRect.right - containerRect.left;
		const elementCenterXInContainer = elementLeftInContainer + (elementRect.width / 2);
		const containerCenterX = containerWidth / 2;

		let left: number;
		left = elementCenterXInContainer < containerCenterX
			? elementLeftInContainer
			: elementRightInContainer - SRT_PREVIEW_WIDTH;

		const containerLeftPadding = getComputedStyle(container).getPropertyValue('padding-left');
		const minLeft = parseInt(containerLeftPadding, 10);
		const maxLeft = containerWidth - SRT_PREVIEW_WIDTH;
		left = Math.max(minLeft, Math.min(maxLeft, left));

		setSrtPreviewCoords({ top, left });
	};

	const menuItems: MenuItem[] = [
		{
			handleClick: () => handleEditLocation(id),
			iconName: ICONS.EDIT,
			text: 'Edit',
		},
		{
			handleClick: handleCopyLocation,
			iconName: ICONS.TEMPLATES,
			text: 'Copy Onsite Link',
		},
		{
			disabled: isSrtLocation,
			checkboxValue: isSrtLocation ? true : advancedAudio,
			handleClick: () => handleToggleSetting(id, advancedAudio, 'advancedAudio'),
			text: 'Advanced Audio',
		},
		{
			checkboxValue: resolution1080p,
			handleClick: () => handleToggleSetting(id, resolution1080p, 'resolution1080p'),
			text: 'Enable 1080p',
		},
		{
			handleClick: () => handleDeleteLocation(id),
			iconName: ICONS.TRASH,
			text: 'Delete Location',
			color: COLORS.RED,
		},
	];

	useEffect(() => {
		if (showSrtPreview && inShot) {
			setShowSrtPreview(false);
		}
	}, [showSrtPreview, inShot]);

	return (
		<>
			{isDragging && <div className="video-thumb-drag-placeholder" />}
			{srtStream && srtStream.status === SrtStreamStatus.active && showSrtPreview && (
				<div className="srt-preview-wrapper" style={{ ...srtPreviewCoords, position: 'absolute' }}>
					<div className="srt-preview" ref={srtPreviewRef}>
						<div className="srt-preview-header">
							<label className="bl-font-l1 truncate-overflow margin-right-small">{locationName}</label>
							<ButtonClose noPadding onClick={() => setShowSrtPreview(false)} />
						</div>
						<SrtPreview srtStream={srtStream} />
					</div>
				</div>
			)}
			<div
				ref={(ref) => {
					dragRef(ref);
					videoThumbRef.current = ref;
				}}
				className={classNames('video-thumb onsite-location', {
					'is-dragging': isDragging,
					'cursor-grab': canDrag,
					'showing-menu': showMenu,
				})}
				onContextMenu={(e) => handleShowMenu(e)}
			>
				<div className="video-wrapper">
					<div className="video-overlay flex-center">
						{locationStream && <FeedDebugOverlay feedWebRtcId={locationStream?.id} />}
						{showMenu && (
							<Portal targetId="portal-onsite-feed-menu">
								<ContextMenu
									menuPosition={menuPosition}
									menuItems={menuItems}
									isShowing={showMenu}
									clickInitiator={menuDotsRef}
									onMenuBlur={() => setShowMenu(false)}
								/>
							</Portal>
						)}
						{host && location === FeedsTab && (
							<div
								className="onsite-feed-menu"
								onClick={(e) => handleShowMenu(e)}
								ref={menuDotsRef}
							>
								<Icon
									name={ICONS.THREE_DOTS_HORIZONTAL}
									size={16}
									color={COLORS.WHITE}
								/>
							</div>
						)}
						{host && (
							<div className="onsite-feed-controls">
								<button
									className={classNames({
										disabled: !locationStream && !srtStream,
										off: !hasAudio,
									})}
									onClick={(e) => {
										e.stopPropagation();
										e.currentTarget.blur();
										if (srtStream) {
											handleUpdateSrtStream({ muted: !srtStream.muted });
										} else if (locationStream) {
											toggleAudio(locationStream.id);
										}
									}}
								>
									<Icon
										name={hasAudio ? ICONS.MICROPHONE_ON : ICONS.MICROPHONE_OFF}
										size={16}
										color={COLORS.TEXT_GRAY_200}
									/>
								</button>
								<button
									className={classNames({
										disabled: !locationStream && !srtStream,
										off: !hasVideo,
									})}
									onClick={(e) => {
										e.stopPropagation();
										e.currentTarget.blur();
										if (srtStream) {
											handleUpdateSrtStream({ videoMuted: !srtStream.videoMuted });
										} else if (locationStream) {
											toggleVideo(locationStream?.id);
										}
									}}
								>
									<Icon
										name={hasVideo ? ICONS.CAMERA_ON : ICONS.CAMERA_OFF}
										size={16}
										color={COLORS.TEXT_GRAY_200}
									/>
								</button>
								<button onClick={handleOpenLocationUrl}>
									<Icon
										name={ICONS.MONITOR}
										size={16}
										color={COLORS.TEXT_GRAY_200}
									/>
								</button>
								{
									srtStream 
									&& srtStream.status === SrtStreamStatus.active 
									&& (
									<button
										className={classNames({ disabled: inShot })}
										onClick={() => {
											calculateSrtPreviewPosition();
											setShowSrtPreview(true);
										}}
									>
										<Icon
											name={ICONS.EYE_SHOWN}
											size={16}
											color={COLORS.TEXT_GRAY_200}
										/>
									</button>
								)}
							</div>
						)}
						{locationStream && (
							<video
								autoPlay
								muted
								playsInline
								onPlaying={handlePlaying}
								ref={(ref: any) => {
									if (ref && ref.srcObject !== locationStream.srcObject) {
										const refRef = ref;
										refRef.srcObject = locationStream.srcObject;
										cleanupVideoStreamElement(ref);
									}
								}}
								style={{
									height: '100%',
									maxHeight: '100%',
									maxWidth: '100%',
								}}
							/>
						)}
						<div className={classNames('poster', { active: !locationStream || !hasVideo })}>
							{srtStream?.status === SrtStreamStatus.active && hasVideo ? (
								<div className="active-srt">
									<div className="active-srt-gradient" />
									<span>Active</span>
								</div>
							) : (
								<img
									className="poster-image"
									src={safetyImage}
									alt={locationName}
									onError={({ currentTarget }) => {
										currentTarget.src = DefaultSafetyImage;
									}}
								/>
							)}
						</div>
						<div className="video-overlay-bottom">
							<div className="onsite-location">
								<label className="bl-font-l1">{locationName}</label>
								{(locationStream || srtStream) && !hasAudio && (
									<Icon name={ICONS.MICROPHONE_OFF} color={COLORS.WHITE} size={16} />
								)}
							</div>
						</div>
					</div>
					<div className="onsite-location-thumbnail-gradient" />
					<div className={classNames('selection-border', inShot && 'in-shot')} />
				</div>
			</div>
		</>
	);
}
